import React, { useState } from "react";
import axios from "axios";
import { Send, Loader2, CheckCircle2, ChevronDown, Check } from "lucide-react";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "./ui/command";
import { cn } from "../lib/utils";
import { useToast } from "../hooks/use-toast";
import { useLanguage } from "../i18n/LanguageContext";
import { statesWithCities, indianStates } from "../data/mock";
import { countryCodes, defaultCountry } from "../data/countryCodes";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function CountryCodeSelect({ value, onChange }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="lead-country-code"
          className="flex items-center gap-1.5 h-full pl-3.5 pr-2.5 text-[#12284B] font-medium shrink-0 focus:outline-none"
        >
          <span className="text-base leading-none">{value.flag}</span>
          <span>{value.code}</span>
          <ChevronDown className="h-3.5 w-3.5 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-64 bg-white border-[#12284B]/15" align="start">
        <Command>
          <CommandInput placeholder="Search country or code" className="text-[#12284B]" />
          <CommandList className="max-h-72">
            <CommandEmpty>No match found</CommandEmpty>
            <CommandGroup>
              {countryCodes.map((c) => (
                <CommandItem
                  key={`${c.name}-${c.code}`}
                  value={`${c.name} ${c.code}`}
                  onSelect={() => { onChange(c); setOpen(false); }}
                  className="text-[#12284B] aria-selected:bg-[#E84B1C] aria-selected:text-white cursor-pointer gap-2"
                  data-testid={`country-opt-${c.name.replace(/\s+/g, "-").toLowerCase()}`}
                >
                  <span className="text-base leading-none">{c.flag}</span>
                  <span className="flex-1 truncate">{c.name}</span>
                  <span className="opacity-70">{c.code}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function SearchableSelect({ value, onChange, options, placeholder, disabled, triggerClass, searchPlaceholder, emptyText, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          data-testid={testid}
          className={cn(triggerClass, "flex w-full items-center justify-between gap-2 border px-3 text-sm disabled:opacity-60 disabled:cursor-not-allowed")}
        >
          <span className={cn("truncate", !value && "text-black/40")}>{value || placeholder}</span>
          <ChevronDown className="h-4 w-4 opacity-50 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[--radix-popover-trigger-width] bg-white border-[#12284B]/15" align="start">
        <Command>
          <CommandInput placeholder={searchPlaceholder} className="text-[#12284B]" />
          <CommandList className="max-h-72">
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {options.map((o) => (
                <CommandItem
                  key={o}
                  value={o}
                  onSelect={() => { onChange(o); setOpen(false); }}
                  className="text-[#12284B] aria-selected:bg-[#E84B1C] aria-selected:text-white cursor-pointer"
                >
                  <Check className={cn("mr-2 h-4 w-4", value === o ? "opacity-100" : "opacity-0")} />
                  {o}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

const empty = {
  full_name: "",
  hotel_name: "",
  mobile: "",
  email: "",
  city: "",
  state: "",
  rooms: "",
  service: "",
  message: "",
};

export default function LeadForm() {
  const [form, setForm] = useState(empty);
  const [cityOther, setCityOther] = useState("");
  const [stateOther, setStateOther] = useState("");
  const [mobileError, setMobileError] = useState("");
  const [country, setCountry] = useState(defaultCountry);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const { toast } = useToast();
  const { t } = useLanguage();
  const f = t.form;

  const update = (k) => (e) =>
    setForm((prev) => ({ ...prev, [k]: e.target ? e.target.value : e }));

  const handleStateChange = (value) => {
    setForm((prev) => ({ ...prev, state: value, city: "" }));
    setCityOther("");
  };

  const handleMobileChange = (e) => {
    const digits = e.target.value.replace(/\D/g, "").slice(0, 10);
    setForm((prev) => ({ ...prev, mobile: digits }));
    if (mobileError && digits.length === 10) setMobileError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.mobile || !form.email) {
      toast({ title: f.reqTitle, description: f.reqDesc });
      return;
    }
    if (!/^\d{10}$/.test(form.mobile)) {
      setMobileError(f.mobileInvalid);
      return;
    }
    const finalCity = form.city === "Other" ? cityOther : form.city;
    const finalState = form.state === "Other" ? stateOther : form.state;
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, {
        ...form,
        mobile: `${country.code} ${form.mobile}`,
        city: finalCity,
        state: finalState,
        rooms: form.rooms ? Number(form.rooms) : null,
      });
      setDone(true);
      setForm(empty);
      setCityOther("");
      setStateOther("");
      setMobileError("");
      toast({ title: f.thankyouTitle, description: f.thankyouMsg });
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast({ title: f.errTitle, description: typeof detail === "string" ? detail : f.errDesc, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="bg-white border border-[#E84B1C]/40 rounded-2xl p-10 text-center shadow-lg shadow-[#12284B]/10">
        <div className="mx-auto h-16 w-16 rounded-full bg-[#E84B1C]/15 flex items-center justify-center">
          <CheckCircle2 size={34} className="text-[#E84B1C]" />
        </div>
        <h3 className="mt-5 font-head text-2xl font-bold text-[#12284B]">{f.thankyouTitle}</h3>
        <p className="mt-3 text-black/70">{f.thankyouMsg}</p>
        <button
          onClick={() => setDone(false)}
          className="mt-6 text-[#E84B1C] font-head font-semibold hover:underline"
        >
          {f.another}
        </button>
      </div>
    );
  }

  const field =
    "bg-white border-[#12284B]/20 text-[#12284B] placeholder:text-black/40 focus-visible:ring-[#E84B1C] focus-visible:ring-offset-0 h-12 rounded-xl";

  return (
    <form
      onSubmit={handleSubmit}
      className="bg-white border border-[#12284B]/12 rounded-2xl p-6 sm:p-8 shadow-xl shadow-[#12284B]/10"
    >
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label className="text-[#12284B] mb-1.5 block">{f.fullName}</Label>
          <Input className={field} placeholder={f.ph.name} value={form.full_name} onChange={update("full_name")} />
        </div>
        <div>
          <Label className="text-[#12284B] mb-1.5 block">{f.hotelName}</Label>
          <Input className={field} placeholder={f.ph.property} value={form.hotel_name} onChange={update("hotel_name")} />
        </div>
        <div>
          <Label className="text-[#12284B] mb-1.5 block">{f.mobile}</Label>
          <div className={`${field} flex items-center p-0 overflow-hidden border`}>
            <CountryCodeSelect value={country} onChange={setCountry} />
            <div className="h-6 w-px bg-[#12284B]/15 shrink-0" />
            <input
              className="flex-1 min-w-0 h-full bg-transparent pl-3 pr-3.5 text-[#12284B] placeholder:text-black/40 focus:outline-none"
              type="tel"
              inputMode="numeric"
              maxLength={10}
              placeholder={f.ph.mobile}
              value={form.mobile}
              onChange={handleMobileChange}
              data-testid="lead-mobile-input"
            />
          </div>
          {mobileError && (
            <p className="mt-1.5 text-sm text-red-600" data-testid="lead-mobile-error">
              {mobileError}
            </p>
          )}
        </div>
        <div>
          <Label className="text-[#12284B] mb-1.5 block">{f.email}</Label>
          <Input className={field} type="email" placeholder={f.ph.email} value={form.email} onChange={update("email")} />
        </div>
        <div>
          <Label className="text-[#12284B] mb-1.5 block">{f.state}</Label>
          <SearchableSelect
            value={form.state}
            onChange={handleStateChange}
            options={[...indianStates, "Other"]}
            placeholder={f.ph.selectState}
            searchPlaceholder={f.ph.selectState}
            emptyText={f.other}
            triggerClass={field}
            testid="lead-state-select"
          />
          {form.state === "Other" && (
            <Input
              className={`${field} mt-2`}
              placeholder={f.ph.otherState}
              value={stateOther}
              onChange={(e) => setStateOther(e.target.value)}
            />
          )}
        </div>
        <div>
          <Label className="text-[#12284B] mb-1.5 block">{f.city}</Label>
          <SearchableSelect
            value={form.city}
            onChange={update("city")}
            options={[...(statesWithCities[form.state] || []), "Other"]}
            placeholder={form.state ? f.ph.selectCity : f.ph.selectStateFirst}
            searchPlaceholder={f.ph.selectCity}
            emptyText={f.other}
            disabled={!form.state}
            triggerClass={field}
            testid="lead-city-select"
          />
          {form.city === "Other" && (
            <Input
              className={`${field} mt-2`}
              placeholder={f.ph.otherCity}
              value={cityOther}
              onChange={(e) => setCityOther(e.target.value)}
            />
          )}
        </div>
        <div>
          <Label className="text-[#12284B] mb-1.5 block">{f.rooms}</Label>
          <Input className={field} type="number" min="0" placeholder={f.ph.rooms} value={form.rooms} onChange={update("rooms")} />
        </div>
      </div>

      <div className="mt-4">
        <Label className="text-[#12284B] mb-1.5 block">{f.service}</Label>
        <Select value={form.service} onValueChange={update("service")}>
          <SelectTrigger className={field}>
            <SelectValue placeholder={f.ph.select} />
          </SelectTrigger>
          <SelectContent className="bg-white border-[#12284B]/15 text-[#12284B]">
            {f.serviceOptions.map((o) => (
              <SelectItem key={o} value={o} className="text-[#12284B] focus:bg-[#E84B1C] focus:text-white">
                {o}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="mt-4">
        <Label className="text-[#12284B] mb-1.5 block">{f.message}</Label>
        <Textarea
          className="bg-white border-[#12284B]/20 text-[#12284B] placeholder:text-black/40 focus-visible:ring-[#E84B1C] focus-visible:ring-offset-0 rounded-xl min-h-[110px]"
          placeholder={f.ph.message}
          value={form.message}
          onChange={update("message")}
        />
      </div>

      <button
        type="submit"
        disabled={loading}
        className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#E84B1C] hover:bg-[#F0673E] disabled:opacity-70 text-white font-head font-semibold text-lg px-8 py-4 rounded-xl transition-all duration-300 hover:scale-[1.02]"
      >
        {loading ? (
          <>
            <Loader2 size={20} className="animate-spin" /> {f.sending}
          </>
        ) : (
          <>
            <Send size={20} /> {f.submit}
          </>
        )}
      </button>
    </form>
  );
}
